import React, { useState, useEffect, useRef } from 'react'
import { useOutletContext } from 'react-router-dom'
import { MessageCircle, Send, Users, Plus, X, Mail } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function PortalMessages() {
  const { team, userRole, canManage, token } = useOutletContext()
  const [conversations, setConversations] = useState([])
  const [activeConversation, setActiveConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    if (team?.team_id) {
      fetchConversations()
    }
  }, [team?.team_id])

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.conversation_id)
    }
  }, [activeConversation])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/conversations/team/${team.team_id}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })
      if (res.ok) {
        const data = await res.json()
        const convos = data.conversations || data || []
        setConversations(convos)
        
        // Auto-select team chat if exists
        const teamChat = convos.find(c => c.is_team_chat || c.type === 'team')
        if (teamChat) {
          setActiveConversation(teamChat)
        } else if (convos.length > 0) {
          setActiveConversation(convos[0])
        }
      }
    } catch (err) {
      console.error('Error fetching conversations:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchMessages = async (conversationId) => {
    try {
      const res = await fetch(`${API_URL}/api/conversations/${conversationId}/messages`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })
      if (res.ok) {
        const data = await res.json()
        setMessages(data.messages || data || [])
      }
    } catch (err) {
      console.error('Error fetching messages:', err)
    }
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || !activeConversation) return

    setSending(true)
    try {
      const res = await fetch(`${API_URL}/api/conversations/${activeConversation.conversation_id}/messages`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Environment': 'production'
        },
        body: JSON.stringify({ content: newMessage })
      })

      if (res.ok) {
        setNewMessage('')
        fetchMessages(activeConversation.conversation_id)
      }
    } catch (err) {
      console.error('Error sending message:', err)
    } finally {
      setSending(false)
    }
  }

  const handleSendEmail = async (e) => {
    e.preventDefault()
    if (!emailSubject.trim() || !emailBody.trim()) {
      alert('Please fill in subject and message')
      return
    }

    setSendingEmail(true)
    try {
      const res = await fetch(`${API_URL}/api/teams/${team.team_id}/send-email`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Environment': 'production'
        },
        body: JSON.stringify({ 
          subject: emailSubject,
          body: emailBody,
          recipients: 'all' // or could be 'staff', 'parents'
        })
      })

      if (res.ok) {
        alert('Email sent successfully!')
        setShowEmailModal(false)
        setEmailSubject('')
        setEmailBody('')
      } else {
        const error = await res.json()
        alert(error.detail || 'Failed to send email')
      }
    } catch (err) {
      console.error('Error sending email:', err)
      alert('Failed to send email')
    } finally {
      setSendingEmail(false)
    }
  }

  const formatTime = (dateStr) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now - date
    
    if (diff < 60000) return 'Just now'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
    if (diff < 86400000) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Messages</h1>
          <p className="text-zinc-400 mt-1">Team communication</p>
        </div>
        
        {canManage && (
          <button
            onClick={() => setShowEmailModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-xl font-medium hover:bg-zinc-700 transition-colors"
          >
            <Mail className="w-4 h-4" />
            Send Email Blast
          </button>
        )}
      </div>

      {/* Chat Container */}
      <div className="flex-1 bg-zinc-800 rounded-xl border border-zinc-700 flex overflow-hidden">
        {/* Conversations List */}
        <div className="w-72 border-r border-zinc-700 flex flex-col">
          <div className="p-4 border-b border-zinc-700">
            <h3 className="font-semibold text-white">Chats</h3>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-4 text-center text-zinc-400 text-sm">
                No conversations yet
              </div>
            ) : (
              conversations.map(convo => (
                <button
                  key={convo.conversation_id}
                  onClick={() => setActiveConversation(convo)}
                  className={`w-full p-4 text-left border-b border-zinc-700 hover:bg-zinc-900 transition-colors ${
                    activeConversation?.conversation_id === convo.conversation_id ? 'bg-zinc-900' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      convo.is_team_chat || convo.type === 'team' ? 'bg-black' : 'bg-zinc-800'
                    }`}>
                      {convo.is_team_chat || convo.type === 'team' ? (
                        <Users className="w-5 h-5 text-white" />
                      ) : (
                        <MessageCircle className="w-5 h-5 text-zinc-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">
                        {convo.name || convo.title || 'Team Chat'}
                      </p>
                      {convo.last_message && (
                        <p className="text-sm text-zinc-400 truncate">
                          {convo.last_message.content}
                        </p>
                      )}
                    </div>
                    {convo.unread_count > 0 && (
                      <span className="w-5 h-5 bg-black text-white text-xs rounded-full flex items-center justify-center">
                        {convo.unread_count}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 flex flex-col">
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-zinc-700 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  activeConversation.is_team_chat || activeConversation.type === 'team' ? 'bg-black' : 'bg-zinc-800'
                }`}>
                  {activeConversation.is_team_chat || activeConversation.type === 'team' ? (
                    <Users className="w-5 h-5 text-white" />
                  ) : (
                    <MessageCircle className="w-5 h-5 text-zinc-300" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-white">
                    {activeConversation.name || activeConversation.title || 'Team Chat'}
                  </h3>
                  <p className="text-sm text-zinc-400">
                    {activeConversation.member_count || activeConversation.members?.length || 0} members
                  </p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-zinc-400">
                    <div className="text-center">
                      <MessageCircle className="w-12 h-12 text-zinc-500 mx-auto mb-2" />
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  </div>
                ) : (
                  messages.map((message, idx) => (
                    <div 
                      key={message.message_id || idx}
                      className={`flex ${message.is_mine ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${message.is_mine ? 'order-2' : ''}`}>
                        {!message.is_mine && (
                          <p className="text-xs text-zinc-400 mb-1 ml-1">
                            {message.sender_name || 'Unknown'}
                          </p>
                        )}
                        <div className={`px-4 py-2.5 rounded-2xl ${
                          message.is_mine 
                            ? 'bg-black text-white rounded-br-md' 
                            : 'bg-zinc-800 text-white rounded-bl-md'
                        }`}>
                          <p>{message.content}</p>
                        </div>
                        <p className={`text-xs text-zinc-500 mt-1 ${message.is_mine ? 'text-right mr-1' : 'ml-1'}`}>
                          {formatTime(message.created_at || message.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-zinc-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2.5 bg-zinc-800 rounded-xl focus:ring-2 focus:ring-black focus:bg-zinc-800 outline-none transition-all"
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="px-4 py-2.5 bg-black text-white rounded-xl hover:bg-zinc-700 transition-colors disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-zinc-400">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-zinc-500 mx-auto mb-4" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm">Choose a chat from the sidebar to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email Blast Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-2xl shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <h3 className="text-lg font-semibold text-white">Send Email to Team</h3>
              <button 
                onClick={() => setShowEmailModal(false)}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <form onSubmit={handleSendEmail} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-200 mb-1">Subject *</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-4 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none"
                  placeholder="Email subject"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-200 mb-1">Message *</label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={6}
                  className="w-full px-4 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none resize-none"
                  placeholder="Type your message..."
                  required
                />
              </div>
              
              <p className="text-sm text-zinc-400">
                This email will be sent to all team members including staff and parents.
              </p>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEmailModal(false)}
                  className="flex-1 px-4 py-2.5 border border-zinc-700 rounded-xl font-medium hover:bg-zinc-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingEmail}
                  className="flex-1 px-4 py-2.5 bg-black text-white rounded-xl font-medium hover:bg-zinc-700 transition-colors disabled:opacity-50"
                >
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
